import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import website_logo from './images/Kuhuk_photo_plaza.jpg';
import wandrobe_logo from './images/wandrobe-logo.png';
import apple_icon_disabled from './images/apple-icon-disabled.svg';
import mobiloitte_logo from './images/mobiloitte-logo.jpeg';
import { RESUME_URL, RESUME_FILENAME, openResumePreview } from './utils/Resume';

const timeline = [
  {
    company: "Wandrobe",
    logo: wandrobe_logo,
    role: "Founder & Engineer",
    dates: "Jul 2025 – Present",
    tagline: "your wardrobe, styled by AI",
    link: "https://wandrobe.com/",
    linkLabel: "Visit Website",
    storeLink: "https://play.google.com/store/apps/details?id=dev.kuhuk.wandrobe_main_app_cmp&pli=1",
    caseStudy: [
      {
        heading: "the problem",
        text: "I like being put together every day, but I could never keep track of what I owned, what actually worked together, or what I'd already worn that week.",
      },
      {
        heading: "the approach",
        text: "Users upload multiple photos of their clothes at once — Wandrobe's AI sees and categorizes each item automatically, building a digital wardrobe. From there, it generates daily outfit suggestions based on the user's plan for the day and the weather. For trips, users can plan multiple outfits per day and get an auto-generated packing list. Outfits can also be logged on any date, past or future, so planning a week — or a year — ahead is just as easy as looking back.",
      },
      {
        heading: "what's live",
        text: "Wandrobe is live on the Play Store today, with iOS in the works — designed, built, and engineered solo, end to end.",
      },
    ],
  },
  {
    company: "Network18 Media & Investments",
    logo: "https://upload.wikimedia.org/wikipedia/commons/7/73/Network_18_2016.svg",
    role: "SDE 2, Android",
    dates: "Jun 2024 – Jul 2025",
    bullets: [
      "Led GA4 analytics implementation for News18 (10M+ downloads) and CNBC TV18 (1M+ downloads)",
      "Contributed to video shorts (ExoPlayer) and horizontal swiper experiences (Jetpack Compose) in the News18 app, part of a broader effort that drove an 82% increase in DAU",
      "Optimized the ExoPlayer framework for live TV playback, troubleshooting buffering and latency issues at 10M+ user scale",
      "Contributed to Git workflow improvements, code reviews, technical mentoring, and R&D initiatives",
    ],
    appLinks: [
      { label: "View News18 App", url: "https://play.google.com/store/apps/details?id=com.divum.ibn&hl=en_IN" },
      { label: "View CNBC App", url: "https://play.google.com/store/apps/details?id=com.network18.cnbctv18&hl=en_IN" },
    ],
  },
  {
    company: "Internshala",
    logo: "https://internshala.com/static/images/internshala_og_image.jpg",
    role: "SDE 2, Android",
    dates: "Sep 2019 – Nov 2023",
    bullets: [
      "Led end-to-end ownership of multiple Android product areas for the Internshala app (5M+ downloads)",
      "Drove MVP adoption and dependency-injection patterns, improving scalability and maintainability",
      "Collaborated with backend, product, and design teams to deliver stable and scalable product features",
      "Maintained a 99.9% crash-free rate through architectural improvements and preventive stability fixes",
      "Conducted code reviews and contributed to engineering standards across the Android team",
    ],
    appLinks: [
      { label: "View Internshala App", url: "https://play.google.com/store/apps/details?id=com.internshala.app&hl=en_IN" },
    ],
  },
  {
    company: "EzeiaTech Systems",
    logo: "https://ezeiatech.com/wp-content/uploads/2024/01/ezeiatech-logo.png",
    role: "SDE, Android",
    dates: "Jan 2019 – Sep 2019",
    bullets: [
      "Contributed to the architecture and development of the Tak Android app for India Today",
      "Worked closely with the testing and product teams to understand requirements and translate them into technical specifications",
      "Built and iterated on core app features based on product and QA feedback, ensuring a stable, release-ready experience",
    ],
    appLinks: [
      { label: "View TAK App", url: "https://play.google.com/store/apps/details?id=com.indiatoday.mobiletak&hl=en_IN" },
    ],
  },
  {
    company: "Mobiloitte",
    logo: mobiloitte_logo,
    role: "Business Development Executive",
    dates: "Mar 2018 – Oct 2018",
    bullets: [
      "Researched and authored 10 in-depth technical articles on blockchain and crypto for Cryptassist",
      "Topics spanned smart contracts & Solidity, DAOs, ICOs, exchange platforms, and DDoS protection",
    ],
    link: "https://medium.com/@cryptassist_In",
    linkLabel: "Read on Medium",
  },
];

const alsoBuilt = {
  logo: website_logo,
  title: "This Very Site",
  description: "The one you're scrolling right now — every layout, animation, and line of copy (yes, even this one) designed and built solo.",
  note: "you're soaking in it 👀",
};

const skillGroups = [
  {
    category: "Core Mobile",
    skills: ["Kotlin", "Android", "Jetpack Compose", "Kotlin Multiplatform (KMP/KMM)", "Compose Multiplatform", "Swift Integration"],
  },
  {
    category: "Backend & Cloud",
    skills: ["Go (Golang)", "AWS Lambda", "DynamoDB", "S3", "AWS CloudWatch", "API Gateway", "REST APIs", "GraphQL", "Serverless Architecture", "GCP", "Cloudflare"],
  },
  {
    category: "AI & LLMs",
    skills: ["Gemini API", "OpenAI API", "Vertex AI", "Prompt Engineering", "Android ML Kit", "AI-assisted Product Workflows"],
  },
  {
    category: "Architecture",
    skills: ["MVVM", "Clean Architecture", "Code Architecture", "System Design", "Offline-first Sync", "SQLDelight", "Ktor", "Coroutines"],
  },
  {
    category: "Leadership & Product",
    skills: ["Firebase Analytics", "Google Analytics (GA4)", "Crashlytics", "RevenueCat", "Google Play Console", "Postman", "Git", "Code Reviews", "Technical Documentation", "Team Leadership", "Product Thinking", "UI/UX Direction"],
  },
];

const timelineStats = [
  { number: "16M+", label: "downloads across shipped apps" },
  { number: "99.9%", label: "crash-free rate at Internshala" },
  { number: "82%", label: "DAU increase from News18 video shorts & swiper" },
  { number: "7+ yrs", label: "professional software engineering" },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6 },
};

const Coding = () => {
  return (
    <div className="div-home text-color-dark">

      {/* Career Timeline */}
      <section className="section">
        <h2 className="section-title">career timeline</h2>
        <p className="section-subtitle">seven years, four companies, one startup</p>

        <div className="hero-highlights work-highlights">
          {timelineStats.map((stat) => (
            <div className="hero-highlight" key={stat.label}>
              <span className="hero-highlight-number">{stat.number}</span>
              <span className="hero-highlight-label">{stat.label}</span>
            </div>
          ))}
        </div>

        <a
          href={RESUME_URL}
          download={RESUME_FILENAME}
          onClick={openResumePreview}
          className="pill-button pill-button-primary resume-download-button"
        >
          Download Resume
        </a>

        <div className="timeline">
          {timeline.map((item) => (
            <motion.div
              className={`timeline-item${item.caseStudy ? " timeline-item-wandrobe" : ""}`}
              key={item.company}
              {...fadeUp}
            >
              <div className="timeline-dates">{item.dates}</div>
              <div className="timeline-marker" />
              <div className="timeline-content">
                <img src={item.logo} alt={item.company} className="timeline-logo" />
                <h3 className="timeline-company">{item.company}</h3>
                <p className="timeline-role">{item.role}</p>

                {item.tagline && <p className="timeline-tagline">{item.tagline}</p>}

                {item.bullets && (
                  <ul className="timeline-bullets">
                    {item.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )}

                {item.caseStudy && (
                  <div className="timeline-case-study">
                    {item.caseStudy.map((block) => (
                      <div className="timeline-case-block" key={block.heading}>
                        <h4>{block.heading}</h4>
                        <p>{block.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {item.appLinks && (
                  <div className="timeline-app-links">
                    {item.appLinks.map((appLink) => (
                      <a
                        key={appLink.label}
                        href={appLink.url}
                        target="_blank"
                        rel="noreferrer"
                        className="pill-button pill-button-primary timeline-pill"
                      >
                        {appLink.label}
                      </a>
                    ))}
                  </div>
                )}

                {item.storeLink ? (
                  <div className="timeline-wandrobe-actions">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="pill-button pill-button-primary wandrobe-button timeline-pill"
                    >
                      {item.linkLabel}
                    </a>
                    <div className="timeline-store-row">
                      <a
                        href={item.storeLink}
                        target="_blank"
                        rel="noreferrer"
                        className="pill-button store-button wandrobe-button timeline-pill"
                      >
                        <span className="store-icon store-icon-playstore" />
                        <span className="store-button-text">
                          <span className="store-button-caption">GET IT ON</span>
                          <span className="store-button-brand">Google Play</span>
                        </span>
                      </a>
                      <span className="pill-button pill-button-disabled store-button wandrobe-button timeline-pill">
                        <img src={apple_icon_disabled} alt="" className="store-icon" />
                        <span className="store-button-text">
                          <span className="store-button-caption">COMING SOON TO</span>
                          <span className="store-button-brand">Apple App Store</span>
                        </span>
                      </span>
                    </div>
                  </div>
                ) : (
                  item.link && (
                    <a href={item.link} target="_blank" rel="noreferrer" className="pill-button pill-button-primary timeline-pill">
                      {item.linkLabel}
                    </a>
                  )
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div className="also-built" {...fadeUp}>
          <p className="also-built-label">also built</p>
          <div className="also-built-card">
            <img src={alsoBuilt.logo} alt={alsoBuilt.title} className="also-built-logo" />
            <div>
              <h4>{alsoBuilt.title}</h4>
              <p>{alsoBuilt.description}</p>
              <span className="also-built-note">{alsoBuilt.note}</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Skills */}
      <section className="section">
        <h2 className="section-title">skillset</h2>
        <div className="skill-index">
          {skillGroups.map((group) => (
            <div className="skill-row" key={group.category}>
              <h4 className="skill-row-title">{group.category}</h4>
              <p className="skill-row-list">
                {group.skills.map((skill, i) => (
                  <Fragment key={skill}>
                    <span className="skill-item">{skill}</span>
                    {i < group.skills.length - 1 && <span className="skill-dot">·</span>}
                  </Fragment>
                ))}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Coding;
